import { CommodityDto } from '@billing/app/api'
import { MenuItem, Typography } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { EmptyIndicator, TomTable, useTomTable } from '@tom-ui/ui'
import { PATHS, useTablePreferencesConfig } from '@tom-ui/utils'
import { MRT_ColumnDef } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { CommodityWithParentStatus } from '../../models/commodity.model'
import { CommodityDialogUIStore } from '../../stores/commodity-dialog.ui-store'
import { CommodityFilterUIStore } from '../../stores/commodity-filter.ui-store'
import { CommodityListUIStore } from '../../stores/commodity-list.ui-store'

interface CommoditiesNestedTableProps {
  listStore: CommodityListUIStore
  dialogStore: CommodityDialogUIStore
  filterStore: CommodityFilterUIStore
}
export const CommoditiesNestedTable = observer(
  ({ listStore, dialogStore }: CommoditiesNestedTableProps) => {
    const { t } = useTranslate()
    const navigate = useNavigate()

    const columns = useMemo<MRT_ColumnDef<CommodityWithParentStatus>[]>(
      () => [
        {
          accessorKey: 'code',
          header: t('code', 'Code'),
          Cell: cell => (
            <Typography
              variant='subtitle2'
              color={cell.row.original.isActive ? 'inherit' : 'secondary'}
            >
              {cell.renderedCellValue}
            </Typography>
          ),
        },
        {
          accessorKey: 'name',
          header: t('name', 'Name'),
          Cell: cell => (
            <Typography
              variant='subtitle2'
              color={cell.row.original.isActive ? 'inherit' : 'secondary'}
            >
              {cell.renderedCellValue}
            </Typography>
          ),
        },
        {
          accessorKey: 'description',
          header: t('description', 'Description'),
          Cell: cell => (
            <Typography
              variant='subtitle2'
              color={cell.row.original.isActive ? 'inherit' : 'secondary'}
            >
              {cell.renderedCellValue ?? EmptyIndicator}
            </Typography>
          ),
        },
        {
          accessorKey: 'isActive',
          header: t('active', 'Active'),
          enableSorting: false,
          enableGlobalFilter: false,
          enableColumnFilter: false,
          Cell: cell => (
            <Typography
              variant='subtitle2'
              color={cell.row.original.isActive ? 'inherit' : 'secondary'}
            >
              {cell.renderedCellValue ? t('yes', 'Yes') : t('no', 'No')}
            </Typography>
          ),
        },
        {
          accessorKey: 'isSelectable',
          header: t('selectable', 'Selectable'),
          enableSorting: false,
          enableGlobalFilter: false,
          enableColumnFilter: false,
          Cell: cell => (
            <Typography
              variant='subtitle2'
              color={cell.row.original.isActive ? 'inherit' : 'secondary'}
            >
              {cell.renderedCellValue ? t('yes', 'Yes') : t('no', 'No')}
            </Typography>
          ),
        },
      ],
      [t],
    )

    const getAllExpandedRowIds = useCallback((rows: CommodityDto[], path: number[] = []) => {
      const expandedIds: Record<string, boolean> = {}
      rows.forEach((row, index) => {
        const currentPath = [...path, index]
        expandedIds[currentPath.join('.')] = true

        if (row.childCommodities?.length) {
          Object.assign(expandedIds, getAllExpandedRowIds(row.childCommodities, currentPath))
        }
      })
      return expandedIds
    }, [])

    const { savedPreferences, onPreferencesChange } =
      useTablePreferencesConfig('billing/commodities')

    const table = useTomTable(
      {
        data: listStore.commodities,
        columns,
        enableGrouping: false,
        enableTopToolbar: false,
        getSubRows: row => row.childCommodities,
        renderRowActionMenuItems: ({ row, closeMenu }) => {
          const options = [
            <MenuItem
              key={`item-${row.id}`}
              data-cy={`edit-${row.id}`}
              onClick={() =>
                navigate(
                  `${PATHS.masterData.commoditiesAndPackaging.commodity.url}/${row.original.catalogueId}/${row.original.parentCommodityId}/${row.original.id}`,
                )
              }
            >
              {t('edit', 'Edit')}
            </MenuItem>,
            <MenuItem
              key={`item-${row.id}`}
              data-cy={`edit-${row.id}`}
              onClick={() =>
                navigate(
                  `${PATHS.masterData.commoditiesAndPackaging.commodity.url}/${row.original.catalogueId}/${row.original.id}/new`,
                )
              }
            >
              {t('addANewSubItem', 'Add a new sub-item')}
            </MenuItem>,
          ]

          if (row.original.isParentActive || row.original.isFirstLevel) {
            options.push(
              <MenuItem
                key={`item-${row.id}`}
                data-cy={`edit-${row.id}`}
                onClick={() => {
                  closeMenu()
                  row.original.isActive
                    ? dialogStore.toggleDeactivate(row.original)
                    : dialogStore.toggleActivate(row.original)
                }}
              >
                {row.original.isActive
                  ? t('deactivateItem', 'Deactivate item')
                  : t('activateItem', 'Activate item')}
              </MenuItem>,
            )
          }

          return [
            ...options,
            <MenuItem
              key={`item-${row.id}`}
              data-cy={`edit-${row.id}`}
              onClick={() => {
                closeMenu()
                dialogStore.toggleDelete(row.original)
              }}
            >
              {t('deleteItem', 'Delete item')}
            </MenuItem>,
          ]
        },
      },
      undefined,
      undefined,
      { columns, savedPreferences, onPreferencesChange },
    )

    return <TomTable table={table} />
  },
)
